import React from "react";
import { useRouter } from "next/router";

import {
  Container,
  Wrapper,
  Strong,
  OL,
  LI,
  Heading,
  RomanList,
  GlobalCss,
  TableBox,
  TableHeader,
  TableData,
  Data,
  TableRow,
  Link,
} from "./style";
import { Typography } from "@/foundation/Typography/V1";
import theme from "@/foundation/Theme";
import useDisableIntercom from "@/hooks/useDisableIntercom";

export const FAQ = (props) => {
  const { showHeader = false } = props || {};

  const location = useRouter();
  const isFromAppSource = location?.query;

  const showTopSpace = isFromAppSource?.source !== "app";

  return (
    <Container flexDirection="column">
      {showHeader && (
        <Typography
          fontFamily={"var(--font-500)"}
          color={theme.colors.gray_100}
          variant="heading1"
        >
          Frequently asked questions
        </Typography>
      )}
      <Wrapper flexDirection={"column"} showHeightGap={showTopSpace}>
        <Typography
          variant="paragraph2"
          fontFamily={"var(--font-400)"}
          color={theme.colors.gray_100}
        >
          Hi there,
          <br />
          <br />
          Welcome to Shyft! We are thrilled that you’ve taken this crucial step
          towards building a healthier body and mind for yourself. Please find
          below some frequently asked questions (FAQs) that may help you get
          started with our Yoga packs.
        </Typography>
        <OL pl={"0px"}>
          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>What do I need for class?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  In order to have the best experience during your yoga practice
                  sessions, you should:
                </Typography>
                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                      textAlign="justify"
                    >
                      Bring a laptop, tablet, or a mobile phone. If you
                      anticipate background noise, we recommend using headphones
                      during class
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                      textAlign="justify"
                    >
                      Bring a yoga mat, a towel, and a bottle of water
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                      textAlign="justify"
                    >
                      Practice in a space, where you can lay the mat on the
                      floor comfortably, and move around the mat freely
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                      textAlign="justify"
                    >
                      Practice in a space, where you are unlikely to be
                      disturbed for the duration of the class
                    </Typography>
                  </LI>
                </OL>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  We also recommend you stay on mute for the duration of the
                  class, and ask any questions you might have before the next
                  class. The trainer is always available 15 minutes before the
                  class to answer member queries
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  We also recommend that you keep your video on for the duration
                  of the class. This enables the trainer to guide your practice,
                  and where needed, correct your posture
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>What should I wear for my class?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  We recommend that you wear comfortable clothing, which
                  facilitates maximum movement of your body, as that will allow
                  you to stretch your body most effectively during your yoga
                  practice
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>How do I join my classes?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  On our app, as well as our website, you will see a join button
                  appear against all classes coming up in the next 24 hours
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  This button will become active
                  <Strong> 10 minutes before the scheduled start time </Strong>
                  of the class, and {""}
                  <Strong>
                    remain active for 10 minutes after the class has started.
                  </Strong>{" "}
                  You will be able to join your class only within this window
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>I love Shyft classes. Can I refer my friends?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  Yes, we would love it if you can tell your friends about us.
                  In fact, we will give you{" "}
                  <Strong>
                    six additional classes, for every successful referral
                  </Strong>{" "}
                  of at least a one-month pack (a successful referral is one
                  where your friend has paid for their pack)
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>
                I tried joining the class within 10 minutes from the start time
                of the class, but was unable to join the class. What should I
                do?
              </Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  If, due to any error, you are unable to join the class using
                  the join button, you should{" "}
                  <Strong>
                    reach out to support using the ‘Talk to Coach’ Section
                  </Strong>{" "}
                  on the homepage, and the customer support team will help
                  provide a speedy resolution for this. We recommending taking a
                  screenshot of any error message you receive, to help the team
                  troubleshoot this effectively, and sharing it with them{" "}
                  <Strong>
                    {" "}
                    I got logged out mid-session, how do I re-join?{" "}
                  </Strong>
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  If you were able to join the session at the beginning, the
                  join button will remain active for you throughout the class
                  duration. In case you get logged out for any reason, you can
                  join back again using the join button on either the website or
                  the app
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>How do I mark my attendance for class?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  As soon as you join your class, whether through the mobile app
                  or through the desktop website, our system automatically marks
                  your attendance, and you do not need to manually inform or
                  update anyone
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>
                I missed a class, or I need to miss a class, what do I do?
              </Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  We understand that you may miss classes sometimes, on account
                  of unexpected circumstances such as personal or family
                  emergencies, sickness including, but not limited to, COVID, or
                  last-minute work or personal commitments. Therefore Shyft
                  allows you to{" "}
                  <Strong> ‘carry forward’ up to 25% of your classes* </Strong>{" "}
                  for free with every pack
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  A carry forward is when a class gets added to the pack
                  automatically, in lieu of missed classes. The{" "}
                  <Strong>
                    {" "}
                    carry forward allowance depends on your purchased pack.
                  </Strong>{" "}
                  Details are given in the table below:
                </Typography>
              </LI>
            </OL>

            <TableBox mt="16px">
              <TableHeader>
                <Data variant="paragraph2" fontFamily="var(--font-700)">
                  Pack Details
                </Data>
                <Data variant="paragraph2" fontFamily="var(--font-700)">
                  Carry-Forwards Allowed
                </Data>
              </TableHeader>
              <TableData>
                <TableRow>
                  <Data variant="paragraph2">8 class-pack</Data>
                  <Data variant="paragraph2">2 classes</Data>
                </TableRow>
                <TableRow>
                  <Data variant="paragraph2">12 class-pack</Data>
                  <Data variant="paragraph2">3 classes</Data>
                </TableRow>
                <TableRow>
                  <Data variant="paragraph2">36 class-pack</Data>
                  <Data variant="paragraph2">12 classes</Data>
                </TableRow>
                <TableRow>
                  <Data variant="paragraph2">72 class-pack</Data>
                  <Data variant="paragraph2">24 classes</Data>
                </TableRow>
                <TableRow>
                  <Data variant="paragraph2">144 class-pack</Data>
                  <Data variant="paragraph2">48 classes</Data>
                </TableRow>
              </TableData>
            </TableBox>

            <span
              style={{
                marginTop: -50,
              }}
            >
              * for class packs with 36 classes or more, we allow 33% carry
              forwards
            </span>
            <br />
            <br />
            <Typography
              fontFamily={"var(--font-400)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              Please note:
            </Typography>

            <OL>
              <RomanList>
                Classes 'carried forward' are added back to your pack
                automatically
              </RomanList>
              <RomanList>
                You can see both the used, as well as availed carried forward
                classes in both the app and the website
              </RomanList>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>Can I make changes to my pack?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  When purchasing a pack, we ask you to choose your preferred
                  start date, as well as your preferred time and weekly
                  frequency for the pack. Please note that we reserve a slot for
                  you based on your selection, once you finalize a purchase with
                  us. Therefore,{" "}
                  <Strong>
                    {" "}
                    we treat your selection as final and binding,
                  </Strong>
                  and are <Strong> not able to make changes to this </Strong>
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>Can I get a refund for my pack?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  We curate small-batches of classes, tailored to your needs.
                  Therefore, all{" "}
                  <Strong> program sales are final, and non-refundable </Strong>
                  , in full or in part
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>What is included with my pack?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  When you enroll for a Yoga pack with Shyft, you also get
                  access to some <Strong> pre-recorded </Strong> content on the
                  app. These videos are available to you, free of cost, for the{" "}
                  <Strong> duration that your pack is active</Strong>.
                </Typography>
              </LI>
            </OL>
          </LI>

          <LI>
            <Heading variant="paragraph2" color={theme.colors.gray_100}>
              <Strong>My issue is not listed here. How can I get help?</Strong>
            </Heading>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  If you have any questions or need any other help, you can
                  reach out to us by either talking to our live Customer Support
                  Team through the ‘Talk To Coach’ Section on the App homepage,
                  or by writing an email to us at:
                  <Link href="mailto:help@betheshyft.com">
                    help@betheshyft.com
                  </Link>
                </Typography>
              </LI>

              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                  textAlign="justify"
                >
                  We recommend including your registered phone number and email,
                  along with a brief description of your issue
                </Typography>
              </LI>
            </OL>
          </LI>
        </OL>
      </Wrapper>
      <GlobalCss />
    </Container>
  );
};

export function FAQMobile() {
  useDisableIntercom();
  return <FAQ />;
}

export default FAQ;
